import { Compiler, Injectable, Injector, NgModuleFactory } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { map, switchMap } from 'rxjs/operators'
import {
  OpenAddGuestDialog,
  OpenAddHoldDialog,
  OpenEditHoldDialog,
  OpenAddInquiryOverlay,
  OpenAddInvoiceOverlay,
  OpenAddRentalDialog,
  OpenAddRentalMiltiDialog,
  OpenAddRentalParentChildDialog,
  OpenGuestOverlay,
  OpenInquiryOverlay,
  OpenInventoryOverlay,
  OpenInvoiceOverlay,
  OpenRentalOverlay,
  OpenAddTaskDialog,
  OpenEditTaskDialog,
  OpenAddExpenseDialog,
  OpenEditExpenseDialog,
  OpenAddRateDialog,
  OpenEditRateDialog,
  OpenAddChannelDialog,
  OpenAddCustomChannelDialog,
  OpenImportCalendarDialog,
  OpenAddExtraDialog,
  OpenEditExtraDialog,
  OpenAddIncidentDialog,
  OpenEditIncidentDialog,
  OpenAddChargeDialog,
  OpenEditChargeDialog,
  OpenAddLosDiscountDialog,
  OpenEditLosDiscountDialog,
  OpenAddDiscountCodeDialog,
  OpenEditDiscountCodeDialog,
  OpenAddTemplateDialog,
  OpenEditTemplateDialog,
  OpenAddBookingFormulaDialog,
  OpenEditBookingFormulaDialog,
  OpenRateAutomationDialog,
  OpenTaskAutomationOverlay,
  OpenBookingAutomationOverlay,
  OpenDocumentAutomationOverlay,
  OpenInvoiceAutomationOverlay,
  OpenMessageAutomationOverlay,
  OpenReviewAutomationOverlay,
  OpenExpenseAutomationOverlay,
  OpenGroupRateDialog,
  OpenAddVendorDialog,
  OpenEditVendorDialog,
  OpenAddDataFeedDialog,
  OpenAddDocumentDialog,
  OpenAddEmailForwardingDialog,
  OpenAddSharingCalendarDialog,
  OpenAddExportCalendarDialog,
  OpenChannelAutomationDialog,
  OpenMinStayAutomationDialog,
  OpenGapNightAutomationDialog,
  OpenLastMinuteAutomationDialog,
  OpenDraftTasksApprovalDialog,
  OpenChannelWizardDialog,
  OpenChannelConnectionWizardDialog,
  OpenChannelConnectionDialog,
  OpenChannelDialog,
  OpenEditInventoryDialog,
  OpenAddWebsiteDialog,
  OpenAutomationWizardOverlay,
} from '@tv3/store/overlay/overlay.actions'
import { GuestService } from '@tv3/store/guest/guest.service'
import { GetGuestComplete } from '@tv3/store/guest/guest.actions'
import { Guest } from '@tv3/store/guest/guest.model'
import { Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { MatDialogConfig } from '@angular/material/dialog'
import { from, Observable } from 'rxjs'
import { DialogService } from '@tokeet-frontend/tv3-platform'
import * as lodash from 'lodash'
import { ComponentType } from '@angular/cdk/overlay'
import { dialogConfigs, SystemEntityDialogType } from './overlay.model'

@Injectable()
export class OverlayEffects {
  openInvoiceOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenInvoiceOverlay),
        switchMap(({ invoiceId, invoices }) =>
          this.openOverlay(
            SystemEntityDialogType.EditInvoice,
            { invoiceId, invoices },
            { panelClass: 'drawer-full-90' }
          )
        )
      ),
    { dispatch: false }
  )

  addInvoice$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddInvoiceOverlay),
        switchMap(({ data }) =>
          this.getDialogServiceInstance(SystemEntityDialogType.AddInvoice).pipe(
            switchMap((service) => service?.addInvoice?.(data?.type))
          )
        )
      ),
    { dispatch: false }
  )

  openInquiryOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenInquiryOverlay),
        switchMap((data) =>
          this.openOverlay(SystemEntityDialogType.EditInquiry, data, { panelClass: 'drawer-full-90' })
        )
      ),
    { dispatch: false }
  )

  addInquiry$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddInquiryOverlay),
        switchMap(({ params }) => this.openWithDialogService(SystemEntityDialogType.AddInquiry, [params]))
      ),
    { dispatch: false }
  )

  openGuestOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenGuestOverlay),
        switchMap(({ guestId }) => this.guestService.get(guestId)),
        switchMap((guest) => {
          this.store.dispatch(GetGuestComplete({ guest }))
          return this.openOverlay(
            SystemEntityDialogType.EditGuest,
            { guest: new Guest(guest) },
            { panelClass: 'drawer-full-70' }
          )
        })
      ),
    { dispatch: false }
  )

  addGuest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddGuestDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddGuest))
      ),
    { dispatch: false }
  )

  addRental$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddRentalDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddRental))
      ),
    { dispatch: false }
  )

  addRentalMulti$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddRentalMiltiDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddRentalMulti))
      ),
    { dispatch: false }
  )

  addRentalParent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddRentalParentChildDialog),
        switchMap(({ parentId }) => this.openWithDialogService(SystemEntityDialogType.AddRentalParentChild, [parentId]))
      ),
    { dispatch: false }
  )

  openRentalOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenRentalOverlay),
        switchMap(({ rental, rentalId, activeTab, highlightedImages }) =>
          this.openOverlay(
            SystemEntityDialogType.EditRental,
            { rentalId: rentalId || rental.id, activeTab, highlightedImages },
            { panelClass: 'drawer-full-80' }
          )
        )
      ),
    { dispatch: false }
  )

  openInventoryOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenInventoryOverlay),
        switchMap(() => this.openOverlay(SystemEntityDialogType.Inventory, {}, { panelClass: 'drawer-full-60' }))
      ),
    { dispatch: false }
  )

  openEditInventoryOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenEditInventoryDialog),
        switchMap(({ inventory }) => this.openWithDialogService(SystemEntityDialogType.EditInventory, [inventory]))
      ),
    { dispatch: false }
  )

  // Website
  addWebsite$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddWebsiteDialog),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.AddWebsite, [data]))
      ),
    { dispatch: false }
  )

  // Hold
  addHold$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddHoldDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddHoldEvent))
      ),
    { dispatch: false }
  )

  editHold$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenEditHoldDialog),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.EditHoldEvent, [data]))
      ),
    { dispatch: false }
  )

  // Task
  addTask$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddTaskDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddTask))
      ),
    { dispatch: false }
  )

  editTask$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenEditTaskDialog),
        switchMap(({ taskId }) => this.openWithDialogService(SystemEntityDialogType.EditTask, [taskId]))
      ),
    { dispatch: false }
  )

  draftTasksApproval$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenDraftTasksApprovalDialog),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.DraftTasksApproval, [data]))
      ),
    { dispatch: false }
  )

  // Expense
  addExpense$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddExpenseDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddExpense))
      ),
    { dispatch: false }
  )

  editExpense$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenEditExpenseDialog),
        switchMap((data) => this.openWithDialogService(SystemEntityDialogType.EditExpense, [data]))
      ),
    { dispatch: false }
  )

  // Rate
  addRate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddRateDialog),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.AddRate, [data]))
      ),
    { dispatch: false }
  )

  editRate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenEditRateDialog),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.EditRate, [data]))
      ),
    { dispatch: false }
  )

  groupRate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenGroupRateDialog),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.GroupRate, [data]))
      ),
    { dispatch: false }
  )

  // Channel
  addChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddChannelDialog),
        switchMap(() =>
          this.getDialogServiceInstance(SystemEntityDialogType.AddChannel).pipe(
            switchMap((service) => service?.connectChannel('api')?.afterClosed())
          )
        )
      ),
    { dispatch: false }
  )

  addCustomChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddCustomChannelDialog),
        switchMap(() =>
          this.getDialogServiceInstance(SystemEntityDialogType.AddChannel).pipe(
            switchMap((service) => service?.connectChannel('custom')?.afterClosed())
          )
        )
      ),
    { dispatch: false }
  )

  importCalendar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenImportCalendarDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.ImportCalendar))
      ),
    { dispatch: false }
  )

  addSharingCalendar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddSharingCalendarDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddSharingCalendar))
      ),
    { dispatch: false }
  )

  addExportCalendar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddExportCalendarDialog),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.AddExportCalendar, [data]))
      ),
    { dispatch: false }
  )

  openChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenChannelDialog),
        switchMap(({ data }) =>
          this.getDialogServiceInstance(SystemEntityDialogType.OpenChannel).pipe(
            switchMap((service) => service?.openSide(data.channel))
          )
        )
      ),
    { dispatch: false }
  )

  openChannelConnection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenChannelConnectionDialog),
        switchMap(({ data }) =>
          this.getDialogServiceInstance(SystemEntityDialogType.OpenChannelConnection).pipe(
            switchMap((service) => service?.openSide(data.connection, data.wizard ?? true))
          )
        )
      ),
    { dispatch: false }
  )

  openChannelWizard$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenChannelWizardDialog),
        switchMap(({ data }) =>
          this.getDialogServiceInstance(SystemEntityDialogType.OpenChannelWizard).pipe(
            switchMap((service) => service?.connect(data.channel))
          )
        )
      ),
    { dispatch: false }
  )

  openChannelConnectionWizard$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenChannelConnectionWizardDialog),
        switchMap(({ data }) =>
          this.getDialogServiceInstance(SystemEntityDialogType.OpenChannelConnectionWizard).pipe(
            switchMap((service) => service?.openWizard(data.channel, data.connection))
          )
        )
      ),
    { dispatch: false }
  )

  // Extra
  addExtra$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddExtraDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddExtra))
      ),
    { dispatch: false }
  )

  editExtra$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenEditExtraDialog),
        switchMap((data) => this.openWithDialogService(SystemEntityDialogType.EditExtra, [data]))
      ),
    { dispatch: false }
  )

  // Incident
  addIncident$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddIncidentDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddIncident))
      ),
    { dispatch: false }
  )

  editIncident$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenEditIncidentDialog),
        switchMap(({ incidentId }) => this.openWithDialogService(SystemEntityDialogType.EditIncident, [incidentId]))
      ),
    { dispatch: false }
  )

  // Charge
  addCharge$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddChargeDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddCharge))
      ),
    { dispatch: false }
  )

  editCharge$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenEditChargeDialog),
        switchMap((data) => this.openWithDialogService(SystemEntityDialogType.EditCharge, [data]))
      ),
    { dispatch: false }
  )

  // LOS discount
  addLosDiscount$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddLosDiscountDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddLosDiscount))
      ),
    { dispatch: false }
  )

  editLosDiscount$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenEditLosDiscountDialog),
        switchMap(({ losDiscount }) =>
          this.openWithDialogService(SystemEntityDialogType.EditLosDiscount, [losDiscount])
        )
      ),
    { dispatch: false }
  )

  // Discount Code
  addDiscountCode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddDiscountCodeDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddDiscountCode))
      ),
    { dispatch: false }
  )

  editDiscountCode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenEditDiscountCodeDialog),
        switchMap(({ discountCode }) =>
          this.openWithDialogService(SystemEntityDialogType.EditDiscountCode, [discountCode])
        )
      ),
    { dispatch: false }
  )

  // template
  addTemplate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddTemplateDialog),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.AddTemplate, [data]))
      ),
    { dispatch: false }
  )

  editTemplate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenEditTemplateDialog),
        switchMap(({ template }) => this.openWithDialogService(SystemEntityDialogType.EditTemplate, [template]))
      ),
    { dispatch: false }
  )

  // Vendor
  addVendor$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddVendorDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddVendor))
      ),
    { dispatch: false }
  )

  editVendor$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenEditVendorDialog),
        switchMap(({ vendor }) => this.openWithDialogService(SystemEntityDialogType.EditVendor, [vendor]))
      ),
    { dispatch: false }
  )
  // Data Feed
  addDataFeed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddDataFeedDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddDataFeed))
      ),
    { dispatch: false }
  )
  // Email Forwarding
  addEmailForwarding$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddEmailForwardingDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddEmailForwarding))
      ),
    { dispatch: false }
  )
  // Formula
  addBookingFormula$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddBookingFormulaDialog),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.AddBookingFormula, [data || {}]))
      ),
    { dispatch: false }
  )
  editBookingFormula$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenEditBookingFormulaDialog),
        switchMap(({ bookingFormula }) =>
          this.openWithDialogService(SystemEntityDialogType.EditBookingFormula, [bookingFormula])
        )
      ),
    { dispatch: false }
  )
  addDocument$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAddDocumentDialog),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddDocument))
      ),
    { dispatch: false }
  )

  // Automation
  openAutomationWizardOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenAutomationWizardOverlay),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.OpenAutomationWizard, [data || {}]))
      ),
    { dispatch: false }
  )

  addTaskAutomationOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenTaskAutomationOverlay),
        switchMap(() =>
          this.openOverlay(SystemEntityDialogType.AddTaskAutomation, {}, { panelClass: 'drawer-full-70' })
        )
      ),
    { dispatch: false }
  )

  addBookingAutomationOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenBookingAutomationOverlay),
        switchMap(() =>
          this.openOverlay(SystemEntityDialogType.AddBookingAutomation, {}, { panelClass: 'drawer-full-70' })
        )
      ),
    { dispatch: false }
  )

  addDocumentAutomationOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenDocumentAutomationOverlay),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddDocumentAutomation))
      ),
    { dispatch: false }
  )

  addInvoiceAutomationOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenInvoiceAutomationOverlay),
        switchMap(() => this.openWithDialogService(SystemEntityDialogType.AddInvoiceAutomation))
      ),
    { dispatch: false }
  )

  addMessageAutomationOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenMessageAutomationOverlay),
        switchMap(() =>
          this.openOverlay(SystemEntityDialogType.AddMessageAutomation, {}, { panelClass: 'drawer-full-70' })
        )
      ),
    { dispatch: false }
  )

  addReviewAutomationOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenReviewAutomationOverlay),
        switchMap(() =>
          this.openOverlay(SystemEntityDialogType.AddReviewAutomation, {}, { panelClass: 'drawer-full-70' })
        )
      ),
    { dispatch: false }
  )

  addExpenseAutomationOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenExpenseAutomationOverlay),
        switchMap(() =>
          this.openOverlay(SystemEntityDialogType.AddExpenseAutomation, {}, { panelClass: 'drawer-full-70' })
        )
      ),
    { dispatch: false }
  )

  addRateAutomationOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenRateAutomationDialog),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.AddRateAutomation, [data]))
      ),
    { dispatch: false }
  )

  addChannelAutomationOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenChannelAutomationDialog),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.AddChannelAutomations, [data]))
      ),
    { dispatch: false }
  )

  addMinStayAutomationOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenMinStayAutomationDialog),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.AddMinStayAutomations, [data]))
      ),
    { dispatch: false }
  )

  addGapNightAutomationOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenGapNightAutomationDialog),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.AddGapNightAutomations, [data]))
      ),
    { dispatch: false }
  )

  addLastMinuteAutomationOverlay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenLastMinuteAutomationDialog),
        switchMap(({ data }) => this.openWithDialogService(SystemEntityDialogType.AddLastMinuteAutomations, [data]))
      ),
    { dispatch: false }
  )

  constructor(
    private actions$: Actions,
    private compiler: Compiler,
    private injector: Injector,
    private store: Store<fromRoot.State>,
    private guestService: GuestService,
    private dialog: DialogService
  ) {}

  private getDialogServiceInstance(type: SystemEntityDialogType): Observable<any> {
    return from(
      Promise.all([dialogConfigs[type].dialogService(), dialogConfigs[type].module().then((m) => this.getModuleRef(m))])
    ).pipe(map(([dialogService, moduleRef]) => moduleRef.injector.get(dialogService)))
  }

  private openWithDialogService(type: SystemEntityDialogType, params: any[] = []) {
    return this.getDialogServiceInstance(type).pipe(
      switchMap((service) => {
        const res = service.open(...params)
        if (typeof res.afterClosed === 'function') {
          return res.afterClosed()
        }
        return res
      })
    )
  }

  private openOverlay(type: SystemEntityDialogType, params: any, dialogConfig: MatDialogConfig = {}): Observable<any> {
    return from(this.loadComponent(type)).pipe(
      switchMap(({ component, injector, componentFactoryResolver }) =>
        this.dialog
          .openDrawer(
            component,
            lodash.defaults({}, dialogConfig, { injector, componentFactoryResolver, data: params })
          )
          .afterClosed()
      )
    )
  }

  private async loadComponent(type: SystemEntityDialogType) {
    const { component, module } = dialogConfigs[type]
    return this.wrapLazyComponentModule(await component(), await module())
  }

  async wrapLazyComponentModule<T>(component: ComponentType<T>, module: any) {
    const moduleRef = await this.getModuleRef(module)
    return {
      component,
      injector: moduleRef.injector,
      componentFactoryResolver: moduleRef.componentFactoryResolver,
    }
  }

  async getModuleRef(module: any) {
    const moduleFactory = await this.loadModuleFactory(module)
    const moduleRef = moduleFactory.create(this.injector)
    return moduleRef
  }

  private async loadModuleFactory(t: any) {
    if (t instanceof NgModuleFactory) {
      return t // AOT
    } else {
      return await this.compiler.compileModuleAsync(t)
    }
  }
}
